import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Toolbar,
  Tooltip
} from '@mui/material'
import {
  Close,
  ZoomIn,
  ZoomOut,
  Download,
  Fullscreen,
  PictureAsPdf
} from '@mui/icons-material'

const PdfViewer = ({ 
  open, 
  onClose, 
  pdfUrl, 
  title = "Visualizar PDF",
  fileName = "documento.pdf"
}) => {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [zoom, setZoom] = useState(100)

  const handleLoad = () => {
    setLoading(false)
    setError(null)
  }

  const handleError = () => {
    setLoading(false)
    setError('Error al cargar el PDF')
  }

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 25, 200))
  }

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 25, 50))
  }

  const handleDownload = () => {
    if (pdfUrl) {
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = fileName
      link.target = '_blank'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const handleFullscreen = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank')
    }
  }

  const resetState = () => {
    setLoading(true)
    setError(null)
    setZoom(100)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PictureAsPdf color="error" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* Toolbar */}
      <Toolbar variant="dense" sx={{ borderBottom: 1, borderColor: 'divider', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
          {fileName}
        </Typography>
        
        <Tooltip title="Alejar">
          <IconButton size="small" onClick={handleZoomOut} disabled={zoom <= 50}>
            <ZoomOut />
          </IconButton>
        </Tooltip>
        
        <Typography variant="body2" sx={{ mx: 1, minWidth: 50, textAlign: 'center' }}>
          {zoom}%
        </Typography>
        
        <Tooltip title="Acercar">
          <IconButton size="small" onClick={handleZoomIn} disabled={zoom >= 200}>
            <ZoomIn />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Pantalla completa">
          <IconButton size="small" onClick={handleFullscreen}>
            <Fullscreen />
          </IconButton>
        </Tooltip>
        
        <Tooltip title="Descargar">
          <IconButton size="small" onClick={handleDownload}>
            <Download />
          </IconButton>
        </Tooltip>
      </Toolbar>

      <DialogContent sx={{ p: 0, overflow: 'hidden', position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.paper',
              zIndex: 1
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Cargando PDF...
              </Typography>
            </Box>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'background.paper',
              zIndex: 1
            }}
          >
            <Alert severity="error" sx={{ maxWidth: 400 }}>
              <Typography variant="body2">
                {error}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Intenta descargar el archivo o abrirlo en pantalla completa.
              </Typography>
            </Alert>
          </Box>
        )}

        {pdfUrl && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              backgroundColor: '#f5f5f5',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              p: 2
            }}
          >
            <iframe
              src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=1&zoom=${zoom}`}
              style={{
                width: `${zoom}%`,
                height: '100%',
                minHeight: '600px',
                border: 'none',
                backgroundColor: 'white',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }}
              onLoad={handleLoad}
              onError={handleError}
              title={title}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={handleDownload} startIcon={<Download />}>
          Descargar
        </Button>
        <Button onClick={handleClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default PdfViewer
